"use client";

import React from "react";

import type { FilterPill } from "@/components/results/FilterSection";
import { ResultsContentLayout } from "@/components/results/ResultsContentLayout";
import { ResultsPageHeader } from "@/components/results/ResultsPageHeader";
import { ResultsStickyFiltersBar } from "@/components/results/ResultsStickyFiltersBar";
import type { SortOrder } from "@/components/results/sort-utils";

import type { ShopperFilters } from "@/lib/shopper-search";

export interface ResultsExperienceHeaderProps {
  searchParams: ShopperFilters | null;
  hasResults: boolean;
  searchTextQuery: string | null;
  searchBreadcrumbPath: string[] | null;
  isSearching: boolean;
  onCategoryNavigate: (path: string) => void;
  isCategoryNavigationEnabled: boolean;
}

export interface ResultsExperienceStickyFiltersProps {
  isLoadingAssistant: boolean;
  filtersExpanded: boolean;
  setFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  mobileSuggestionsExpanded: boolean;
  setMobileSuggestionsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isSticky: boolean;
  filtersHeight: number;
  filtersSectionTopRef: React.RefObject<HTMLDivElement | null>;
  filtersContentRef: React.RefObject<HTMLDivElement | null>;
  activeFilters: FilterPill[];
  suggestedFilters: FilterPill[];
  selectedBrands: FilterPill[];
  similarToBrandsPills: FilterPill[];
  activeBrandsCount: number;
  assistantJustification?: string | null;
  onMobileFiltersOpen: () => void;
  onClearSuggestedFilters: () => void;
  onClearSuggestedBrands: () => void;
}

export interface ResultsExperienceLayoutProps {
  hasSearchParams: boolean;
  hasProducts: boolean;
  productCount: number;
  appliedFiltersCount: number;
  isMobileFiltersOpen: boolean;
  onMobileFiltersOpenChange: (open: boolean) => void;
  onClearAllFilters: () => void;
  sortOrder: SortOrder;
  isSortMenuOpen: boolean;
  onToggleSortMenu: () => void;
  onSortSelect: (sort: SortOrder) => void;
  sortDropdownListId: string;
  filtersContent: React.ReactNode;
}

export interface ResultsExperienceProps {
  header: ResultsExperienceHeaderProps;
  stickyFilters: ResultsExperienceStickyFiltersProps;
  layout: ResultsExperienceLayoutProps;
  content: React.ReactNode;
}

export function ResultsExperience({
  header,
  stickyFilters,
  layout,
  content,
}: ResultsExperienceProps): React.ReactElement {
  const {
    searchParams,
    hasResults,
    searchTextQuery,
    searchBreadcrumbPath,
    isSearching,
    onCategoryNavigate,
    isCategoryNavigationEnabled,
  } = header;
  const {
    isLoadingAssistant,
    filtersExpanded,
    setFiltersExpanded,
    mobileSuggestionsExpanded,
    setMobileSuggestionsExpanded,
    isSticky,
    filtersHeight,
    filtersSectionTopRef,
    filtersContentRef,
    activeFilters,
    suggestedFilters,
    selectedBrands,
    similarToBrandsPills,
    activeBrandsCount,
    assistantJustification,
    onMobileFiltersOpen,
    onClearSuggestedFilters,
    onClearSuggestedBrands,
  } = stickyFilters;
  const {
    hasSearchParams,
    hasProducts,
    productCount,
    appliedFiltersCount,
    isMobileFiltersOpen,
    onMobileFiltersOpenChange,
    onClearAllFilters,
    sortOrder,
    isSortMenuOpen,
    onToggleSortMenu,
    onSortSelect,
    sortDropdownListId,
    filtersContent,
  } = layout;
  const showStickyFiltersBar =
    isSearching ||
    isLoadingAssistant ||
    activeFilters.length > 0 ||
    suggestedFilters.length > 0 ||
    selectedBrands.length > 0;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <main className="flex-grow">
        {(hasSearchParams || isSearching) && (
          <div className="px-4 pt-0 lg:px-[30px]">
            <ResultsPageHeader
              breadcrumbPath={searchBreadcrumbPath}
              searchTextQuery={searchTextQuery}
              searchParams={searchParams}
              hasResults={hasResults}
              isSearching={isSearching}
              onCategoryNavigate={onCategoryNavigate}
              isCategoryNavigationEnabled={isCategoryNavigationEnabled}
            />

            {showStickyFiltersBar && (
              <ResultsStickyFiltersBar
                isSearching={isSearching}
                isLoadingAssistant={isLoadingAssistant}
                filtersExpanded={filtersExpanded}
                setFiltersExpanded={setFiltersExpanded}
                mobileSuggestionsExpanded={mobileSuggestionsExpanded}
                setMobileSuggestionsExpanded={setMobileSuggestionsExpanded}
                isSticky={isSticky}
                filtersHeight={filtersHeight}
                filtersSectionTopRef={filtersSectionTopRef}
                filtersContentRef={filtersContentRef}
                activeFilters={activeFilters}
                suggestedFilters={suggestedFilters}
                selectedBrands={selectedBrands}
                similarToBrandsPills={similarToBrandsPills}
                activeBrandsCount={activeBrandsCount}
                assistantJustification={assistantJustification}
                appliedFiltersCount={appliedFiltersCount}
                onMobileFiltersClick={onMobileFiltersOpen}
                onClearSuggestedFilters={onClearSuggestedFilters}
                onClearSuggestedBrands={onClearSuggestedBrands}
              />
            )}
          </div>
        )}

        <ResultsContentLayout
          isSearching={isSearching}
          hasProducts={hasProducts}
          productCount={productCount}
          hasSearchParams={hasSearchParams}
          showStickyFiltersBar={showStickyFiltersBar}
          isMobileFiltersOpen={isMobileFiltersOpen}
          onMobileFiltersOpenChange={onMobileFiltersOpenChange}
          appliedFiltersCount={appliedFiltersCount}
          onClearAllFilters={onClearAllFilters}
          sortOrder={sortOrder}
          isSortMenuOpen={isSortMenuOpen}
          onToggleSortMenu={onToggleSortMenu}
          onSortSelect={onSortSelect}
          sortDropdownListId={sortDropdownListId}
          filtersContent={filtersContent}
        >
          {content}
        </ResultsContentLayout>
      </main>
    </div>
  );
}
